import {
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useCallback, useMemo } from 'react';
import { dataModelerSelectCurrentDraftEdits } from '../../../../redux/selectors/dataModeler';
import {
  getModelField,
  temporalFieldRefsToTableColumnsDraftData,
} from '../utils';
import {
  dataModelerAddDraftInstallTemporals,
  dataModelerRemoveDraftInstallTemporals,
  dataModelerRemoveDraftSubject,
  dataModelerSetDraftSubject,
  dataModelerSetDraftSubjectName,
  dataModelerSetInstallTemporalColumn,
} from '../../../../redux/slices/dataModeler';
import { useSubjectNameValidatorFunc } from '../validation';

export interface ISubjectListItem {
  subjectName: string;
  subjectKey: string;
  fieldKey: string;
  columnName: string | null;
  tableColumn: ITableColumnFieldsFragment | null;
  canExtend: boolean;
  isNew: boolean;
}

export const useDimensionOptionalFieldsModel = ({
  schemaObj,
  isEditMode,
  viewKey,
  tableKey,
}: {
  schemaObj: ISchemaDataModelFieldsFragment;
  isEditMode: boolean;
  viewKey: string;
  tableKey: string;
}) => {
  const dispatch = useAppDispatch();
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);

  // subjects
  const existingSubjectsList = useMemo(
    () =>
      schemaObj.subjects
        .filter((subj) => subj.viewKey === viewKey)
        .map((subj) => {
          const fieldMeta = getModelField(schemaObj, subj.fieldKey);
          const tableField = fieldMeta?.tableFields?.find(
            (tableField) => tableField.tableKey === tableKey,
          );
          const tableColumn = !tableField
            ? draftEdits?.draftFieldsData.find(
                (draft) => draft.tableKey === tableKey,
              )?.column ?? null
            : null;
          return {
            subjectName: subj.name,
            subjectKey: subj.subjectKey,
            fieldKey: subj.fieldKey,
            columnName: tableField?.column ?? null,
            tableColumn,
            // pre-existing in the schema model mapping can not be edited,
            // we also don't get a tableColumn in that case
            canExtend: !tableField,
            isNew: false,
          };
        }),
    [draftEdits?.draftFieldsData, schemaObj, tableKey, viewKey],
  );

  const newSubjectsList = useMemo(
    () =>
      draftEdits?.addedSubjects
        .filter((addedSubj) => addedSubj.viewKey === viewKey)
        .map((subj) => {
          const addedTableField =
            draftEdits?.draftFieldsData.find(
              (draft) => draft.addInput?.key === subj.input.fieldKey,
            ) ?? null;
          const extendedTableColumns =
            draftEdits?.draftFieldsData.filter(
              (draft) => draft.extendInput?.fieldKey === subj.input.fieldKey,
            ) ?? [];
          const draftField = [
            ...(addedTableField ? [addedTableField] : []),
            ...extendedTableColumns,
          ].find((tc) => tc.tableKey === tableKey);
          return {
            subjectName: subj.input.subjectName,
            subjectKey: subj.subjectKey,
            fieldKey: subj.input.fieldKey,
            columnName: draftField?.column?.column ?? null,
            tableColumn: draftField?.column ?? null,
            canExtend: !!addedTableField && !draftField?.addInput,
            isNew: true,
          };
        }) ?? [],
    [draftEdits?.addedSubjects, draftEdits?.draftFieldsData, viewKey, tableKey],
  );

  const subjectsList: ISubjectListItem[] = useMemo(
    () => [...existingSubjectsList, ...newSubjectsList],
    [existingSubjectsList, newSubjectsList],
  );

  const subjectRequired =
    schemaObj.subjects.length === 0 &&
    (draftEdits?.addedSubjects?.length ?? 0) === 0;

  const subjectNameValidatorFunc = useSubjectNameValidatorFunc();

  const suggestedNextSubjectName = useMemo(() => {
    if (subjectNameValidatorFunc().safeParse('User').success) {
      return 'User';
    }
    if (subjectNameValidatorFunc().safeParse('Session').success) {
      return 'Session';
    }
    if (subjectNameValidatorFunc().safeParse('Device').success) {
      return 'Device';
    }
    return 'New Subject Name';
  }, [subjectNameValidatorFunc]);

  const addNewSubject = useCallback(
    (subjectName: string, tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetDraftSubject({
          viewKey,
          tableKey,
          subjectName,
          subjectKey: null,
          tableColumn,
        }),
      );
    },
    [dispatch, tableKey, viewKey],
  );

  const removeNewSubject = useCallback(
    (subjectKey: string) => {
      dispatch(
        dataModelerRemoveDraftSubject({
          subjectKey,
        }),
      );
    },
    [dispatch],
  );

  const updateSubjectColumn = useCallback(
    (
      subjectKey: string,
      tableKey: string,
      tableColumn: ITableColumnFieldsFragment,
    ) => {
      const subjectItem = subjectsList.find(
        (subj) => subj.subjectKey === subjectKey,
      );
      if (!subjectItem) {
        return;
      }
      if (subjectItem.canExtend) {
        throw Error('Attempt to extend a dimension field');
      } else {
        dispatch(
          dataModelerSetDraftSubject({
            subjectName: subjectItem.subjectName,
            subjectKey,
            viewKey,
            tableKey,
            tableColumn,
          }),
        );
      }
    },
    [dispatch, viewKey, subjectsList],
  );

  const updateSubjectName = useCallback(
    (subjectKey: string, subjectName: string) => {
      dispatch(dataModelerSetDraftSubjectName({ subjectKey, subjectName }));
    },
    [dispatch],
  );

  // install date
  const installTemporalsData = useMemo(() => {
    if (
      schemaObj.installTemporals &&
      schemaObj.installTemporals.viewKey !== viewKey
    ) {
      return undefined;
    }
    return (
      draftEdits?.editTablesData?.find((draft) => draft.tableKey === tableKey)
        ?.installTemporals ??
      draftEdits?.addedDimensionsData?.find(
        (addedDim) => addedDim.tableKey === tableKey,
      )?.installTemporals ??
      (schemaObj.installTemporals
        ? temporalFieldRefsToTableColumnsDraftData(
            schemaObj.installTemporals,
            tableKey,
          )
        : undefined)
    );
  }, [
    draftEdits?.editTablesData,
    draftEdits?.addedDimensionsData,
    viewKey,
    schemaObj.installTemporals,
    tableKey,
  ]);

  const installTemporalsSetDate = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetInstallTemporalColumn({
          viewKey,
          tableColumn,
          tableKey,
          temporalField: 'date',
        }),
      );
    },
    [dispatch, viewKey, tableKey],
  );

  const installTemporalsInit = useCallback(() => {
    dispatch(
      dataModelerAddDraftInstallTemporals({
        viewKey,
      }),
    );
  }, [dispatch, viewKey]);

  const installTemporalsRemove = useCallback(() => {
    dispatch(
      dataModelerRemoveDraftInstallTemporals({
        viewKey,
        tableKey,
      }),
    );
  }, [dispatch, viewKey, tableKey]);

  const installTemporalsCanInit = useMemo(
    () =>
      !installTemporalsData &&
      !schemaObj.installTemporals &&
      !draftEdits?.editTablesData?.some(
        (editData) => !!editData.installTemporals,
      ) &&
      !draftEdits?.addedDimensionsData?.some(
        (dimData) => !!dimData.installTemporals,
      ),
    [
      draftEdits?.editTablesData,
      draftEdits?.addedDimensionsData,
      installTemporalsData,
      schemaObj.installTemporals,
    ],
  );

  const installTemporalsCanEdit = isEditMode && !schemaObj.installTemporals;
  return {
    // subject
    subjectRequired,
    subjectsList,
    addNewSubject,
    removeNewSubject,
    updateSubjectColumn,
    subjectNameValidatorFunc,
    suggestedNextSubjectName,
    updateSubjectName,
    // install date (optional)
    installTemporalsCanEdit,
    installTemporalsData,
    installTemporalsCanInit,
    installTemporalsInit,
    installTemporalsRemove,
    installTemporalsSetDate,
  };
};
